import { type FormEvent, type ReactNode, useState } from 'react'
import {
  type AttentionItem,
  type Connectivity,
  type NetstatusParams,
  type StatusCard,
  useDashboardNetwork,
  useDashboardPos,
  useNetstatus,
} from '../../../features/dashboard/use-dashboard'
import { Button, EmptyRow, inputCls, td, th } from './controls'
import { SearchIcon } from './icons'
import { AppLink, Badge, Card, cardCls, cardStyle, mono, PageHeader, type Tone } from './ui'

const RANGES = ['1d', '7d', '30d'] as const
type Range = (typeof RANGES)[number]

const LEVEL_TONE: Record<Connectivity, Tone> = { online: 'ok', degraded: 'warn', offline: 'bad', unknown: 'off' }
const LEVEL_LABEL: Record<Connectivity, string> = {
  online: 'Online',
  degraded: 'Degraded',
  offline: 'Offline',
  unknown: 'Unknown',
}
const fgOf = (t: Tone) => `var(--v1-${t})`

const SK = ['a', 'b', 'c', 'd']
const PAGE_SIZE_FALLBACK = 25

function Section({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) {
  return (
    <section className="mt-12">
      <div className="mb-4">
        <h2 className="text-[20px] font-semibold tracking-[-0.01em]">{title}</h2>
        {hint ? <p className="mt-0.5 text-[13px] text-[var(--color-muted)]">{hint}</p> : null}
      </div>
      {children}
    </section>
  )
}

function Segmented<T extends string>({
  value,
  options,
  onChange,
  label,
}: {
  value: T
  options: readonly T[]
  onChange: (v: T) => void
  label: string
}) {
  return (
    <fieldset
      aria-label={label}
      className="m-0 inline-flex min-w-0 rounded-[10px] border-0 bg-[var(--color-surface-2)] p-0.5"
    >
      {options.map((o) => (
        <button
          key={o}
          type="button"
          aria-pressed={o === value}
          onClick={() => onChange(o)}
          className="h-8 rounded-[8px] px-3.5 text-[13px] font-medium transition-colors focus-visible:outline-2 focus-visible:outline-[var(--color-accent)]"
          style={
            o === value
              ? { background: 'var(--color-surface)', color: 'var(--color-fg)', boxShadow: 'var(--v1-shadow)' }
              : { color: 'var(--color-muted)' }
          }
        >
          {o}
        </button>
      ))}
    </fieldset>
  )
}

function Metric({
  label,
  value,
  sub,
  tone,
  children,
}: {
  label: string
  value: ReactNode
  sub?: ReactNode
  tone?: Tone
  children?: ReactNode
}) {
  return (
    <div className={`${cardCls} p-5`} style={cardStyle}>
      <div className="text-[13px] text-[var(--color-muted)]">{label}</div>
      <div
        className="mt-2 text-[30px] font-semibold leading-none tracking-[-0.02em]"
        style={{ color: tone ? fgOf(tone) : undefined }}
      >
        {value}
      </div>
      {sub ? <div className="mt-2 text-[13px] text-[var(--color-muted)]">{sub}</div> : null}
      {children}
    </div>
  )
}

/** Stacked proportion bar with a legend; the one visual we allow ourselves. */
function MixBar({ parts }: { parts: { label: string; value: number; tone: Tone }[] }) {
  const total = parts.reduce((s, p) => s + p.value, 0)
  return (
    <div className="mt-4">
      <div
        className="flex h-2 overflow-hidden rounded-full bg-[var(--color-surface-2)]"
        role="img"
        aria-label={parts.map((p) => `${p.label} ${p.value}`).join(', ')}
      >
        {parts.map((p) =>
          p.value > 0 && total > 0 ? (
            <div key={p.label} style={{ width: `${(p.value / total) * 100}%`, background: fgOf(p.tone) }} />
          ) : null,
        )}
      </div>
      <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-[var(--color-muted)]">
        {parts.map((p) => (
          <li key={p.label} className="inline-flex items-center gap-1.5">
            <span className="size-1.5 rounded-full" style={{ background: fgOf(p.tone) }} />
            {p.label} <span className="font-medium text-[var(--color-fg)]">{p.value}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

function SparkLine({ points }: { points: number[] }) {
  if (points.length < 2) return null
  const min = Math.min(...points)
  const span = Math.max(...points) - min || 1
  const d = points
    .map(
      (p, i) =>
        `${i === 0 ? 'M' : 'L'}${((i / (points.length - 1)) * 100).toFixed(2)},${(22 - ((p - min) / span) * 20).toFixed(2)}`,
    )
    .join(' ')
  return (
    <svg viewBox="0 0 100 24" preserveAspectRatio="none" className="mt-3 h-6 w-full" aria-hidden="true">
      <path
        d={d}
        fill="none"
        stroke="var(--color-muted)"
        strokeWidth="1.5"
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  )
}

function CardsSkeleton({ n }: { n: number }) {
  return (
    <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-4" aria-hidden="true">
      {SK.slice(0, n).map((id) => (
        <div key={id} className={`${cardCls} p-5`} style={cardStyle}>
          <div className="v1-skeleton h-3.5 w-24" />
          <div className="v1-skeleton mt-4 h-8 w-20" />
          <div className="v1-skeleton mt-3 h-3 w-32" />
        </div>
      ))}
    </div>
  )
}

function ErrorBlock({ what, onRetry }: { what: string; onRetry: () => void }) {
  return (
    <div className={`${cardCls} flex items-center justify-between gap-4 px-5 py-4`} role="alert" style={cardStyle}>
      <div>
        <p className="text-[14px] font-medium" style={{ color: 'var(--v1-bad)' }}>
          Couldn't load {what}
        </p>
        <p className="mt-0.5 text-[13px] text-[var(--color-muted)]">The rest of the dashboard is unaffected.</p>
      </div>
      <Button variant="quiet" onClick={onRetry}>
        Try again
      </Button>
    </div>
  )
}

// ---------- attention ----------

function AttentionList({ items }: { items: AttentionItem[] }) {
  const down = items.filter((i) => i.status === 'down').length
  const shown = items.slice(0, 8)
  return (
    <Card
      title="Needs attention"
      aside={
        items.length ? (
          <span className="text-[13px] text-[var(--color-muted)]">
            {down} down, {items.length - down} degraded
          </span>
        ) : undefined
      }
    >
      {items.length === 0 ? (
        <div className="flex items-center gap-3 px-5 py-10">
          <span className="size-2.5 rounded-full" style={{ background: 'var(--v1-ok)' }} />
          <p className="text-[15px] font-medium">All clear. Every tunnel and device is healthy.</p>
        </div>
      ) : (
        <>
          <ul>
            {shown.map((a) => (
              <li key={a.storeCode} className="border-t border-[var(--color-border)] first:border-t-0">
                <AppLink
                  to={`/locations/${encodeURIComponent(a.storeCode)}/network`}
                  className="flex min-h-12 items-center gap-4 px-5 py-2.5 transition-colors hover:bg-[var(--color-surface-2)] focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[var(--color-accent)]"
                >
                  <Badge
                    label={a.status === 'down' ? 'Down' : 'Degraded'}
                    tone={a.status === 'down' ? 'bad' : 'warn'}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[14px] font-medium">{a.storeName}</span>
                    <span className="block text-[12px] text-[var(--color-muted)]">
                      <span style={mono}>{a.storeCode}</span> · {a.vendor === 'cloudflare' ? 'Cloudflare' : 'Meraki'}
                    </span>
                  </span>
                  <span className="text-[13px] text-[var(--color-muted)]">{a.detail}</span>
                  <span aria-hidden="true" className="text-[var(--color-muted)]">
                    ›
                  </span>
                </AppLink>
              </li>
            ))}
          </ul>
          {items.length > shown.length ? (
            <p className="border-t border-[var(--color-border)] px-5 py-3 text-[13px] text-[var(--color-muted)]">
              and {items.length - shown.length} more
            </p>
          ) : null}
        </>
      )}
    </Card>
  )
}

// ---------- connectivity ----------

function RollupCard({ card, active, onSelect }: { card: StatusCard; active: boolean; onSelect: () => void }) {
  const tone = LEVEL_TONE[card.status]
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={active}
      className={`${cardCls} block w-full p-5 text-left transition-colors hover:bg-[var(--color-surface-2)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-accent)]`}
      style={{
        ...cardStyle,
        ...(active ? { borderColor: 'var(--color-accent)', boxShadow: '0 0 0 3px var(--v1-accent-soft)' } : {}),
      }}
    >
      <span className="flex items-center gap-2 text-[13px] text-[var(--color-muted)]">
        <span className="size-2 rounded-full" style={{ background: fgOf(tone) }} />
        {LEVEL_LABEL[card.status]}
      </span>
      <span className="mt-2 block text-[30px] font-semibold leading-none tracking-[-0.02em]">{card.count}</span>
      <span className="mt-2 block text-[13px] text-[var(--color-muted)]">
        {card.pct.toFixed(card.pct % 1 ? 1 : 0)}% of stores
      </span>
    </button>
  )
}

function WanRollup({ title, cards }: { title: string; cards: StatusCard[] }) {
  const parts = cards.map((c) => ({ label: LEVEL_LABEL[c.status], value: c.count, tone: LEVEL_TONE[c.status] }))
  const total = parts.reduce((s, p) => s + p.value, 0)
  return (
    <div className={`${cardCls} p-5`} style={cardStyle}>
      <div className="flex items-baseline justify-between">
        <h3 className="text-[15px] font-semibold">{title}</h3>
        <span className="text-[13px] text-[var(--color-muted)]">{total} links</span>
      </div>
      <MixBar parts={parts} />
    </div>
  )
}

function FleetConnectivity() {
  const [params, setParams] = useState<NetstatusParams>({})
  const [draft, setDraft] = useState('')
  const q = useNetstatus(params)
  const set = (p: Partial<NetstatusParams>) => setParams((cur) => ({ ...cur, page: 1, ...p }))
  const d = q.data

  const filtered = Boolean(params.status || params.search)
  const pages = d ? Math.max(1, Math.ceil(d.total / (d.pageSize || PAGE_SIZE_FALLBACK))) : 1

  const submit = (e: FormEvent) => {
    e.preventDefault()
    set({ search: draft.trim() || undefined })
  }

  return (
    <Section title="Fleet connectivity" hint="Every store, rolled up. Select a status to filter the list.">
      {q.isLoading ? (
        <CardsSkeleton n={3} />
      ) : q.error || !d ? (
        <ErrorBlock what="fleet connectivity" onRetry={() => void q.refetch()} />
      ) : (
        <>
          <div className="grid gap-5 sm:grid-cols-3">
            {d.cards.map((c) => (
              <RollupCard
                key={c.status}
                card={c}
                active={params.status === c.status}
                onSelect={() => set({ status: params.status === c.status ? undefined : c.status })}
              />
            ))}
          </div>
          <div className="mt-5 grid gap-5 md:grid-cols-3">
            <WanRollup title="WAN, all links" cards={d.wanCards} />
            <WanRollup title="WAN 1" cards={d.wan1Cards} />
            <WanRollup title="WAN 2" cards={d.wan2Cards} />
          </div>

          <div className="mt-5">
            <Card
              title="Locations"
              aside={
                <form onSubmit={submit} className="flex items-center gap-2">
                  <div className="relative">
                    <SearchIcon className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-[var(--color-muted)]" />
                    <input
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      placeholder="Search stores"
                      aria-label="Search stores"
                      className={`${inputCls} w-52 pl-9`}
                    />
                  </div>
                  {filtered ? (
                    <Button
                      variant="quiet"
                      onClick={() => {
                        setDraft('')
                        setParams({})
                      }}
                    >
                      Clear
                    </Button>
                  ) : null}
                </form>
              }
            >
              {d.locations.length === 0 ? (
                <EmptyRow>{filtered ? 'No stores match these filters.' : 'No locations to show yet.'}</EmptyRow>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-[14px]">
                    <thead>
                      <tr>
                        <th className={th}>Store</th>
                        <th className={th}>Region</th>
                        <th className={th}>Connectivity</th>
                        <th className={th}>WAN 1</th>
                        <th className={th}>WAN 2</th>
                      </tr>
                    </thead>
                    <tbody>
                      {d.locations.map((l) => (
                        <tr key={l.code} className="transition-colors hover:bg-[var(--color-surface-2)]">
                          <td className={td}>
                            <AppLink to={`/locations/${encodeURIComponent(l.code)}`} className="hover:underline">
                              <span className="font-medium">{l.name}</span>{' '}
                              <span className="text-[12px] text-[var(--color-muted)]" style={mono}>
                                {l.code}
                              </span>
                            </AppLink>
                          </td>
                          <td className={`${td} text-[var(--color-muted)]`}>
                            {[l.city, l.state].filter(Boolean).join(', ') || '-'}
                          </td>
                          <td className={td}>
                            <Badge label={LEVEL_LABEL[l.connectivity]} tone={LEVEL_TONE[l.connectivity]} />
                          </td>
                          <td className={td}>
                            <Badge label={LEVEL_LABEL[l.wan1]} tone={LEVEL_TONE[l.wan1]} />
                          </td>
                          <td className={td}>
                            <Badge label={LEVEL_LABEL[l.wan2]} tone={LEVEL_TONE[l.wan2]} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <footer className="flex h-12 items-center justify-between border-t border-[var(--color-border)] px-5 text-[13px] text-[var(--color-muted)]">
                <span>
                  {d.total} {d.total === 1 ? 'store' : 'stores'} · page {d.page} of {pages}
                </span>
                <span className="flex gap-1">
                  <Button
                    variant="quiet"
                    disabled={d.page <= 1}
                    onClick={() => setParams((c) => ({ ...c, page: d.page - 1 }))}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="quiet"
                    disabled={d.page >= pages}
                    onClick={() => setParams((c) => ({ ...c, page: d.page + 1 }))}
                  >
                    Next
                  </Button>
                </span>
              </footer>
            </Card>
          </div>
        </>
      )}
    </Section>
  )
}

// ---------- network ----------

function NetworkSections({ range }: { range: Range }) {
  const q = useDashboardNetwork(range)
  const d = q.data
  return (
    <>
      <Section title="Attention" hint="Stores whose tunnels or devices need action, worst first.">
        {q.isLoading ? (
          <div className={`${cardCls} p-5`} style={cardStyle} aria-hidden="true">
            <div className="v1-skeleton h-4 w-40" />
            <div className="v1-skeleton mt-5 h-3.5 w-full" />
            <div className="v1-skeleton mt-3 h-3.5 w-5/6" />
            <div className="v1-skeleton mt-3 h-3.5 w-2/3" />
          </div>
        ) : q.error || !d ? (
          <ErrorBlock what="the attention list" onRetry={() => void q.refetch()} />
        ) : (
          <AttentionList items={d.attention} />
        )}
      </Section>

      <Section title="Network health" hint={`Tunnels, devices and WAN links over the last ${range}.`}>
        {q.isLoading ? (
          <CardsSkeleton n={4} />
        ) : q.error || !d ? (
          <ErrorBlock what="network health" onRetry={() => void q.refetch()} />
        ) : (
          <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-4">
            <Metric
              label="Tunnel uptime"
              value={`${d.tunnelHealth.avgUptimePct.toFixed(1)}%`}
              tone={d.tunnelHealth.avgUptimePct >= 99 ? 'ok' : d.tunnelHealth.avgUptimePct >= 95 ? 'warn' : 'bad'}
              sub={
                <>
                  {d.tunnelHealth.currentlyDown} down now
                  {d.tunnelHealth.trendDelta ? (
                    <>
                      {' · '}
                      <span style={{ color: fgOf(d.tunnelHealth.trendDelta > 0 ? 'ok' : 'bad') }}>
                        {d.tunnelHealth.trendDelta > 0 ? '+' : ''}
                        {d.tunnelHealth.trendDelta.toFixed(1)} pts
                      </span>
                    </>
                  ) : null}
                </>
              }
            >
              <SparkLine points={d.tunnelHealth.sparkline} />
            </Metric>
            <Metric
              label="Device issues"
              value={d.deviceHealth.issueCount}
              tone={d.deviceHealth.issueCount ? 'warn' : 'ok'}
              sub={`${d.deviceHealth.unreachable} unreachable`}
            />
            <Metric label="Locations with WAN" value={d.wanLinks.locationsWithWan} sub="by link priority">
              <MixBar
                parts={[
                  { label: 'Primary', value: d.wanLinks.primary, tone: 'ok' },
                  { label: 'Secondary', value: d.wanLinks.secondary, tone: 'warn' },
                  { label: 'Tertiary', value: d.wanLinks.tertiary, tone: 'bad' },
                ]}
              />
            </Metric>
            <Metric label="Tunnel mix" value={d.tunnelMix.total} sub="tunnels, by vendor">
              <MixBar
                parts={[
                  { label: 'Cloudflare up', value: d.tunnelMix.cloudflare.up, tone: 'ok' },
                  {
                    label: 'Cloudflare down',
                    value: d.tunnelMix.cloudflare.total - d.tunnelMix.cloudflare.up,
                    tone: 'bad',
                  },
                  { label: 'Meraki up', value: d.tunnelMix.meraki.up, tone: 'off' },
                  { label: 'Meraki down', value: d.tunnelMix.meraki.total - d.tunnelMix.meraki.up, tone: 'warn' },
                ]}
              />
            </Metric>
          </div>
        )}
      </Section>
    </>
  )
}

// ---------- pos ----------

function PosSection({ range }: { range: Range }) {
  const q = useDashboardPos(range)
  const d = q.data
  return (
    <Section title="Point of sale" hint={`Terminals and payments over the last ${range}.`}>
      {q.isLoading ? (
        <CardsSkeleton n={4} />
      ) : q.error || !d ? (
        <ErrorBlock what="POS health" onRetry={() => void q.refetch()} />
      ) : (
        <>
          <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-4">
            <Metric
              label="Terminals online"
              value={d.terminalStatus.online}
              tone={d.terminalStatus.offline ? 'warn' : 'ok'}
              sub={`of ${d.terminalStatus.total} · ${d.terminalStatus.offline} offline`}
            >
              <MixBar
                parts={[
                  { label: 'Online', value: d.terminalStatus.online, tone: 'ok' },
                  { label: 'Offline', value: d.terminalStatus.offline, tone: 'bad' },
                ]}
              />
            </Metric>
            <Metric
              label="Stale check-ins"
              value={d.staleCheckIns.count}
              tone={d.staleCheckIns.count ? 'warn' : 'ok'}
              sub={`across ${d.staleCheckIns.affectedLocations} locations`}
            />
            <Metric
              label="Unused workstations"
              value={d.unusedWorkstations.count}
              sub={`no activity in ${d.unusedWorkstations.days} days`}
            />
            <Metric label="Payment methods" value={d.paymentMethods.locationsWithAdyen} sub="locations with Adyen">
              <MixBar
                parts={[
                  { label: 'Active', value: d.paymentMethods.active, tone: 'ok' },
                  { label: 'Inactive', value: d.paymentMethods.inactive, tone: 'off' },
                ]}
              />
            </Metric>
          </div>

          <div className="mt-5">
            <Card
              title="Failing terminals"
              aside={<span className="text-[13px] text-[var(--color-muted)]">{d.failingTerminals.length} total</span>}
            >
              {d.failingTerminals.length === 0 ? (
                <div className="flex items-center gap-3 px-5 py-8">
                  <span className="size-2.5 rounded-full" style={{ background: 'var(--v1-ok)' }} />
                  <p className="text-[15px] font-medium">No failing terminals.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-[14px]">
                    <thead>
                      <tr>
                        <th className={th}>Terminal</th>
                        <th className={th}>Location</th>
                        <th className={th}>Issue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {d.failingTerminals.slice(0, 8).map((t) => (
                        <tr key={t.terminalId} className="transition-colors hover:bg-[var(--color-surface-2)]">
                          <td className={td}>
                            <span className="font-medium">{t.model}</span>{' '}
                            <span className="text-[12px] text-[var(--color-muted)]" style={mono}>
                              {t.serialNumber}
                            </span>
                          </td>
                          <td className={`${td} text-[var(--color-muted)]`}>
                            {t.locationName ?? '-'}
                            {t.locationCode ? <span style={mono}> · {t.locationCode}</span> : null}
                          </td>
                          <td className={td}>
                            <Badge
                              label={t.reason === 'not-checking-in' ? 'Not checking in' : 'No activity 24h'}
                              tone={t.reason === 'not-checking-in' ? 'bad' : 'warn'}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </div>
        </>
      )}
    </Section>
  )
}

export function PorcelainDashboard() {
  const [range, setRange] = useState<Range>('30d')
  return (
    <div className="mx-auto max-w-[1200px] pb-12">
      <PageHeader
        title="Overview"
        subtitle="How the fleet is doing right now, and what needs you."
        right={<Segmented label="Time range" value={range} options={RANGES} onChange={setRange} />}
      />
      <NetworkSections range={range} />
      <FleetConnectivity />
      <PosSection range={range} />
    </div>
  )
}
